import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Steps,
  Row,
  Col,
  Divider,
  Typography,
  Card,
  Space,
  Alert,
  App,
  Checkbox,
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  DollarOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Order } from '../../types/orders';
import {
  ClienteFiscal,
  CreateClienteFiscalRequest,
  SolicitarFacturaRequest,
  FacturaResult,
} from '../../types/facturacion';
import {
  useGetClientesFiscalesQuery,
  useCreateClienteFiscalMutation,
  useSolicitarFacturaMutation,
  useRegenerarFacturaMutation,
  useTimbrarFacturaMutation,
  useCancelarFacturaMutation,
  useFacturarTicketDeGlobalMutation,
  useGetUsosCFDIQuery,
  useGetRegimenesFiscalesQuery,
  useGetFormasPagoQuery,
  useGetMetodosPagoQuery,
  useGetFacturacionConfigQuery,
} from '../../services/facturacionApi';
import { useGetSettingQuery } from '../../services/settingsApi';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { getErrorMessage } from '../../utils/errorHelpers';
import { useAppSelector } from '../../hooks/redux';
import { canManageFacturas, isSupportUser } from '../../utils/roleHelpers';
import CancelarFacturaModal, { type CancelarFacturaFormValues } from './CancelarFacturaModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

/**
 * Normalizes SAT payment code to ensure it has leading zero if needed.
 * The SAT XML requires codes like '01', '04' but the catalog may return '1', '4'.
 */
const normalizeSATCode = (code: string): string => {
  // If it's a single digit, add leading zero
  if (code.length === 1 && /^\d$/.test(code)) {
    return '0' + code;
  }
  return code;
};

/**
 * Maps payment method codes from Massrestaurant to SAT payment codes.
 * @param paymentMethodCode - The payment method code from the order (e.g., 'cash', 'card', 'transfer')
 * @returns The corresponding SAT payment code with proper formatting (e.g., '01', '04', '03')
 */
const mapPaymentMethodToSATCode = (paymentMethodCode: string): string | null => {
  // SAT XML requires codes with leading zeros (e.g., '01', '04')
  const codeMapping: Record<string, string> = {
    // Efectivo -> 01 - Efectivo
    cash: '01',
    efectivo: '01',
    // Tarjeta de crédito -> 04 - Tarjeta de crédito
    card: '04',
    credit: '04',
    credit_card: '04',
    tarjeta: '04',
    'tarjeta de credito': '04',
    // Tarjeta de débito -> 28 - Tarjeta de débito
    debit: '28',
    debit_card: '28',
    debito: '28',
    'tarjeta de debito': '28',
    // Transferencia electrónica -> 03 - Transferencia electrónica de fondos
    transfer: '03',
    transferencia: '03',
    bank_transfer: '03',
    spei: '03',
    // Monedero electrónico / Pago móvil -> 31 - Intermediario pagos
    mobile: '31',
    digital: '31',
    wallet: '31',
    // Vales / Bonos -> 08 - Vales de despensa
    voucher: '08',
    vale: '08',
    bono: '08',
  };

  const normalizedCode = paymentMethodCode.toLowerCase().trim();
  return codeMapping[normalizedCode] || null;
};

/**
 * Finds a matching catalog entry, handling the case where catalog has '4' but we need '04'.
 */
const findCatalogMatch = (
  catalogo: Array<{ clave: string }> | undefined,
  value: string
): string | null => {
  if (!catalogo) return null;

  // Try exact match first
  const exactMatch = catalogo.find(item => item.clave === value);
  if (exactMatch) return exactMatch.clave;

  // Try without leading zero (catalog might have '4' instead of '04')
  const withoutLeadingZero = value.replace(/^0/, '');
  const matchWithoutZero = catalogo.find(item => item.clave === withoutLeadingZero);
  if (matchWithoutZero) return matchWithoutZero.clave;

  // Try with leading zero (value might be '4' but catalog has '04')
  if (value.length === 1 && /^\d$/.test(value)) {
    const withLeadingZero = '0' + value;
    const matchWithZero = catalogo.find(item => item.clave === withLeadingZero);
    if (matchWithZero) return matchWithZero.clave;
  }

  return null;
};

/**
 * Determines the SAT payment code from an order's payments.
 * If the order has multiple payments, returns the code of the largest payment.
 * Returns null if no mapping is found (will use default from config).
 */
const getFormaPagoFromOrder = (order?: Order): string | null => {
  if (!order?.payments || order.payments.length === 0) {
    return null;
  }

  // If single payment, use that
  if (order.payments.length === 1) {
    return mapPaymentMethodToSATCode(order.payments[0].paymentMethodCode);
  }

  // If multiple payments, use the one with the largest amount
  const largestPayment = order.payments.reduce((prev, current) =>
    current.amount > prev.amount ? current : prev
  );
  return mapPaymentMethodToSATCode(largestPayment.paymentMethodCode);
};

interface SolicitarFacturaModalProps {
  visible: boolean;
  onCancel: () => void;
  order?: Order;
  onSuccess?: (factura: FacturaResult) => void;
  /** Si se proporciona, el modal funcionará en modo "regenerar" en lugar de "crear" */
  facturaIdToRegenerate?: number;
  /**
   * La orden está en esta factura global timbrada: se factura a nombre del cliente con el
   * flujo del motivo 04 (la global se reexpide sin el ticket y la anterior se cancela).
   */
  facturaGlobalId?: number;
}

const SolicitarFacturaModal: React.FC<SolicitarFacturaModalProps> = ({
  visible,
  onCancel,
  order,
  onSuccess,
  facturaIdToRegenerate,
  facturaGlobalId,
}) => {
  const isRegenerateMode = !!facturaIdToRegenerate;
  const isGlobalMode = !!facturaGlobalId;
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCliente, setSelectedCliente] = useState<ClienteFiscal | null>(null);
  const [isNewCliente, setIsNewCliente] = useState(false);
  const [facturaResult, setFacturaResult] = useState<FacturaResult | null>(null);
  const [defaultFormaPago, setDefaultFormaPago] = useState<string>('01');
  const [defaultMetodoPago, setDefaultMetodoPago] = useState<string>('PUE');
  const [mostrarCancelacion, setMostrarCancelacion] = useState(false);

  // Determinar si UsoCFDI es S01 para mostrar checkbox de desglose IVA
  const currentUsoCFDI = selectedCliente?.usoCFDI || form.getFieldValue('usoCFDI');
  const esS01 = currentUsoCFDI === 'S01';

  // API hooks
  const { data: clientes, isLoading: clientesLoading } = useGetClientesFiscalesQuery();
  const { data: usosCFDI, isLoading: usosCFDILoading } = useGetUsosCFDIQuery();
  const { data: regimenesFiscales, isLoading: regimenesLoading } = useGetRegimenesFiscalesQuery();
  const { data: formasPago, isLoading: formasPagoLoading } = useGetFormasPagoQuery();
  const { data: metodosPago, isLoading: metodosPagoLoading } = useGetMetodosPagoQuery();
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const { data: descripcionNoMapeadosSetting } = useGetSettingQuery('DescripcionProductosNoMapeados');

  const [createCliente, { isLoading: creatingCliente }] = useCreateClienteFiscalMutation();
  const [solicitarFactura, { isLoading: solicitandoFactura }] = useSolicitarFacturaMutation();
  const [regenerarFactura, { isLoading: regenerandoFactura }] = useRegenerarFacturaMutation();
  const [timbrarFactura, { isLoading: timbrandoFactura }] = useTimbrarFacturaMutation();
  const [cancelarFactura, { isLoading: cancelandoFactura }] = useCancelarFacturaMutation();
  const [facturarTicketDeGlobal, { isLoading: facturandoDeGlobal }] = useFacturarTicketDeGlobalMutation();
  const { user } = useAppSelector((state) => state.auth);
  // Cancelar ante el SAT es solo de administración (el backend lo exige; esto evita el 403)
  const puedeCancelar = canManageFacturas(user?.role, isSupportUser(user?.email));

  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setSelectedCliente(null);
      setIsNewCliente(false);
      setFacturaResult(null);
      form.resetFields();

      // Calculate payment defaults for local state
      // mapPaymentMethodToSATCode returns SAT format ('04'), but we need catalog format ('4')
      const detectedFormaPago = getFormaPagoFromOrder(order);

      // Find the matching catalog value for the detected/default forma de pago
      let formaPagoForCatalog = detectedFormaPago || facturacionConfig?.formaPagoDefault || '01';
      if (formasPago && formasPago.length > 0) {
        const matched = findCatalogMatch(formasPago, formaPagoForCatalog);
        if (matched) formaPagoForCatalog = matched;
      }

      const metodoPagoValue = facturacionConfig?.metodoPagoDefault || 'PUE';

      // Update local state for defaults (using catalog format)
      setDefaultFormaPago(formaPagoForCatalog);
      setDefaultMetodoPago(metodoPagoValue);

      // Set default description for unmapped products
      if (descripcionNoMapeadosSetting?.value) {
        form.setFieldsValue({ descripcionNoMapeados: descripcionNoMapeadosSetting.value });
      }
    }
  }, [visible, form, order, facturacionConfig, formasPago, descripcionNoMapeadosSetting]);

  // Set form values when entering step 1 (payment step) and catalogs are loaded
  useEffect(() => {
    if (currentStep === 1 && formasPago && formasPago.length > 0 && metodosPago && metodosPago.length > 0) {
      const currentFormaPago = form.getFieldValue('formaPago');
      const currentMetodoPago = form.getFieldValue('metodoPago');

      // Only set if not already set
      if (!currentFormaPago || !currentMetodoPago) {
        // Find matching catalog value (handles '04' vs '4' mismatch)
        const matchedFormaPago = findCatalogMatch(formasPago, defaultFormaPago) || defaultFormaPago;
        const matchedMetodoPago = findCatalogMatch(metodosPago, defaultMetodoPago) || defaultMetodoPago;

        form.setFieldsValue({
          formaPago: currentFormaPago || matchedFormaPago,
          metodoPago: currentMetodoPago || matchedMetodoPago,
        });
      }
    }
  }, [currentStep, formasPago, metodosPago, form, defaultFormaPago, defaultMetodoPago]);


  const handleClienteSelect = (clienteId: string) => {
    if (clienteId === 'new') {
      setIsNewCliente(true);
      setSelectedCliente(null);
      // Reset form fields for new client
      form.setFieldsValue({
        rfc: undefined,
        razonSocial: undefined,
        nombreComercial: undefined,
        usoCFDI: facturacionConfig?.usoCFDIDefault || undefined,
        regimenFiscal: undefined,
        email: undefined,
        telefono: undefined,
        codigoPostal: undefined,
        calle: undefined,
        numeroExterior: undefined,
        numeroInterior: undefined,
      });
    } else {
      // Find client by facturadorClienteId (not by id, as id is Guid.Empty for all)
      const cliente = clientes?.find((c) => c.facturadorClienteId === Number(clienteId));
      setSelectedCliente(cliente || null);
      setIsNewCliente(false);
      // Set form fields with selected client data
      if (cliente) {
        form.setFieldsValue({
          rfc: cliente.rfc,
          razonSocial: cliente.razonSocial,
          nombreComercial: cliente.nombreComercial,
          usoCFDI: cliente.usoCFDI,
          regimenFiscal: cliente.regimenFiscal,
          email: cliente.email,
          telefono: cliente.telefono,
        });
      }
    }
  };

  const handleStep1Next = () => {
    form.validateFields(['rfc', 'razonSocial', 'usoCFDI', 'regimenFiscal'])
      .then(() => {
        // Set payment values before changing step to ensure they're set when step 1 renders
        const currentFormaPago = form.getFieldValue('formaPago');
        const currentMetodoPago = form.getFieldValue('metodoPago');

        if (!currentFormaPago) {
          form.setFieldsValue({ formaPago: defaultFormaPago });
        }
        if (!currentMetodoPago) {
          form.setFieldsValue({ metodoPago: defaultMetodoPago });
        }

        setCurrentStep(1);
      })
      .catch((err) => {
        console.error('Validation failed:', err);
      });
  };

  const handleSolicitarFactura = async () => {
    try {
      // Get ALL form values (including fields not currently rendered)
      const values = form.getFieldsValue(true);

      let clienteData: ClienteFiscal;

      // Si es cliente nuevo, crearlo primero
      if (isNewCliente) {
        const newClienteRequest: CreateClienteFiscalRequest = {
          rfc: values.rfc,
          razonSocial: values.razonSocial,
          nombreComercial: values.nombreComercial,
          usoCFDI: values.usoCFDI,
          regimenFiscal: values.regimenFiscal,
          email: values.email,
          telefono: values.telefono,
          codigoPostal: values.codigoPostal,
          calle: values.calle,
          numeroExterior: values.numeroExterior,
          numeroInterior: values.numeroInterior,
        };

        const createdCliente = await createCliente(newClienteRequest).unwrap();
        clienteData = createdCliente;
      } else if (selectedCliente) {
        clienteData = selectedCliente;
      } else {
        message.error('Debe seleccionar o crear un cliente fiscal');
        return;
      }

      // Solicitar factura o regenerar
      // Normalize formaPago to SAT format (with leading zero if needed)
      const request: SolicitarFacturaRequest = {
        orderId: order!.id,
        clienteFiscal: clienteData,
        formaPago: normalizeSATCode(values.formaPago || '01'), // Efectivo por defecto
        metodoPago: values.metodoPago || 'PUE', // Pago en una exhibición
        descripcionNoMapeados: values.descripcionNoMapeados?.trim() || undefined,
        desglosarIVA: values.desglosarIVA || false,
      };

      let result: FacturaResult;
      if (isGlobalMode) {
        // Emite la factura del cliente ya timbrada; la global se reexpide sin el ticket.
        result = await facturarTicketDeGlobal(request).unwrap();
        message.success('Factura emitida. La factura global se reexpide sin este ticket.');
      } else if (isRegenerateMode && facturaIdToRegenerate) {
        result = await regenerarFactura({ facturaId: facturaIdToRegenerate, request }).unwrap();
        message.success('Factura regenerada exitosamente');
      } else {
        result = await solicitarFactura(request).unwrap();
        message.success('Factura generada exitosamente');
      }

      setFacturaResult(result);
      setCurrentStep(2);

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error: unknown) {
      console.error('Error al solicitar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al generar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleDownloadPDF = () => {
    if (facturaResult) {
      // Abrir PDF en nueva pestaña
      const pdfUrl = `${window.location.origin}/api${facturaResult.pdfUrl}`;
      window.open(pdfUrl, '_blank');
    }
  };

  const handleDownloadXML = () => {
    if (facturaResult && facturaResult.xmlUrl) {
      // Abrir XML en nueva pestaña
      const xmlUrl = `${window.location.origin}/api${facturaResult.xmlUrl}`;
      window.open(xmlUrl, '_blank');
    }
  };

  const handleTimbrarFactura = async () => {
    if (!facturaResult) return;

    try {
      const result = await timbrarFactura(facturaResult.facturaId).unwrap();

      // Actualizar el estado con los datos de timbrado
      setFacturaResult({
        ...facturaResult,
        uuid: result.uuid,
        fechaTimbrado: result.fechaTimbrado,
        rfcProvCertif: result.rfcProvCertif,
        xmlUrl: result.xmlUrl,
        estado: result.estado,
      });

      message.success('Factura timbrada exitosamente');
    } catch (error: unknown) {
      console.error('Error al timbrar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al timbrar la factura. Verifique que el certificado digital esté vigente.');
      message.error(errorMsg, 8);
    }
  };

  const handleCancelarFactura = async () => {
    if (!facturaResult || !facturaResult.uuid) {
      message.warning('Solo se pueden cancelar facturas timbradas');
      return;
    }

    // El motivo se pregunta en vez de mandar '02' fijo. Ver CancelarFacturaModal.
    setMostrarCancelacion(true);
  };

  const confirmarCancelacion = async (values: CancelarFacturaFormValues) => {
    if (!facturaResult) return;

    try {
      const resultado = await cancelarFactura({
        facturaId: facturaResult.facturaId,
        data: { motivo: values.motivo, uuidSustitucion: values.uuidSustitucion },
      }).unwrap();

      // "Solicitada" no es "cancelada": si el receptor debe aceptarla, el SAT tarda.
      if (resultado.confirmada) message.success(resultado.mensaje);
      else message.info(resultado.mensaje, 8);
      setMostrarCancelacion(false);

      // Actualizar estado
      setFacturaResult({
        ...facturaResult,
        estado: resultado.confirmada ? 'Cancelada' : 'CancelacionEnProceso',
      });
    } catch (error: unknown) {
      console.error('Error al cancelar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al cancelar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleClose = () => {
    onCancel();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Form.Item
              label="Cliente Fiscal"
              name="clienteId"
              rules={[{ required: !isNewCliente, message: 'Seleccione un cliente' }]}
            >
              <Select
                placeholder="Seleccione un cliente o cree uno nuevo"
                onChange={handleClienteSelect}
                loading={clientesLoading}
                showSearch
                optionFilterProp="children"
              >
                <Option value="new">
                  <UserOutlined /> + Crear nuevo cliente
                </Option>
                <Select.OptGroup label="Clientes existentes">
                  {clientes?.map((cliente) => (
                    <Option key={cliente.facturadorClienteId} value={cliente.facturadorClienteId}>
                      {cliente.rfc} - {cliente.razonSocial}
                    </Option>
                  ))}
                </Select.OptGroup>
              </Select>
            </Form.Item>

            {(isNewCliente || selectedCliente) && (
              <>
                <Divider style={{ margin: '12px 0 8px' }}>Datos Fiscales</Divider>

                <Row gutter={8}>
                  <Col xs={24} sm={10}>
                    <Form.Item
                      label="RFC"
                      name="rfc"
                      rules={[
                        { required: true, message: 'RFC requerido' },
                        {
                          pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i,
                          message: 'RFC inválido (12 caracteres para persona moral, 13 para persona física)',
                        },
                      ]}
                      
                      normalize={(value) => value?.toUpperCase()}
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        placeholder="RFC (12 o 13 caracteres)"
                        maxLength={13}
                        disabled={!!selectedCliente}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={14}>
                    <Form.Item
                      label="Razón Social"
                      name="razonSocial"
                      rules={[{ required: true, message: 'Razón social requerida' }]}

                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="Nombre o razón social" disabled={!!selectedCliente} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Uso de CFDI"
                      name="usoCFDI"
                      rules={[{ required: true, message: 'Uso de CFDI requerido' }]}
                      
                      style={{ marginBottom: 8 }}
                    >
                      <Select
                        placeholder="Seleccione uso de CFDI"
                        loading={usosCFDILoading}
                        disabled={!!selectedCliente}
                        showSearch
                        optionFilterProp="children"
                      >
                        {usosCFDI?.map((uso) => (
                          <Option key={uso.clave} value={uso.clave}>
                            {uso.clave} - {uso.descripcion}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Régimen Fiscal"
                      name="regimenFiscal"
                      rules={[{ required: true, message: 'Régimen fiscal requerido' }]}
                      
                      style={{ marginBottom: 8 }}
                    >
                      <Select
                        placeholder="Seleccione régimen fiscal"
                        loading={regimenesLoading}
                        disabled={!!selectedCliente}
                        showSearch
                        optionFilterProp="children"
                      >
                        {regimenesFiscales?.map((regimen) => (
                          <Option key={regimen.clave} value={regimen.clave}>
                            {regimen.clave} - {regimen.descripcion}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { required: true, message: 'Email requerido' },
                        { type: 'email', message: 'Email inválido' },
                      ]}
                      
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="correo@ejemplo.com" disabled={!!selectedCliente} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Teléfono"
                      name="telefono"
                      
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="5512345678" disabled={!!selectedCliente} />
                    </Form.Item>
                  </Col>
                </Row>

                {isNewCliente && (
                  <>
                    <Divider style={{ margin: '8px 0 8px' }}>Dirección Fiscal</Divider>
                    <Row gutter={8}>
                      <Col xs={24} sm={6}>
                        {/* CFDI 4.0 exige el DomicilioFiscalReceptor (C.P.) para timbrar.
                            Aquí no tenía reglas mientras CrearFacturaModal sí lo exigía, así
                            que por esta ruta se podía mandar al PAC un receptor sin C.P. y
                            el rechazo llegaba hasta el timbrado. */}
                        <Form.Item
                          label="Código Postal"
                          name="codigoPostal"
                          rules={[
                            { required: true, message: 'C.P. requerido' },
                            { pattern: /^\d{5}$/, message: '5 dígitos' },
                          ]}
                          style={{ marginBottom: 8 }}
                        >
                          <Input placeholder="12345" maxLength={5} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={18}>
                        <Form.Item label="Calle" name="calle" style={{ marginBottom: 8 }}>
                          <Input placeholder="Nombre de la calle" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8}>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Número Exterior" name="numeroExterior" style={{ marginBottom: 8 }}>
                          <Input placeholder="123" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item label="Número Interior" name="numeroInterior" style={{ marginBottom: 8 }}>
                          <Input placeholder="A" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </div>
        );

      case 1:
        return (
          <div>
            <Alert
              message="Datos de Facturación"
              description={
                <Space direction="vertical">
                  <Text>
                    <strong>Cliente:</strong> {selectedCliente?.razonSocial || form.getFieldValue('razonSocial')}
                  </Text>
                  <Text>
                    <strong>RFC:</strong> {selectedCliente?.rfc || form.getFieldValue('rfc')}
                  </Text>
                  <Text>
                    <strong>Orden:</strong> #{order?.id?.substring(0, 8)}
                  </Text>
                  <Text>
                    <strong>Total:</strong> ${order?.total?.toFixed(2)}
                  </Text>
                </Space>
              }
              type="info"
              style={{ marginBottom: 24 }}
            />

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Forma de Pago"
                  name="formaPago"
                  rules={[{ required: true, message: 'Forma de pago requerida' }]}
                >
                  <Select
                    placeholder="Seleccione forma de pago"
                    loading={formasPagoLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {formasPago?.map((forma) => (
                      <Option key={forma.clave} value={forma.clave}>
                        {forma.clave} - {forma.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Método de Pago"
                  name="metodoPago"
                  rules={[{ required: true, message: 'Método de pago requerido' }]}
                >
                  <Select
                    placeholder="Seleccione método de pago"
                    loading={metodosPagoLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {metodosPago?.map((metodo) => (
                      <Option key={metodo.clave} value={metodo.clave}>
                        {metodo.clave} - {metodo.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {esS01 && (
              <Form.Item
                name="desglosarIVA"
                valuePropName="checked"
                initialValue={false}
                style={{ marginBottom: 16 }}
              >
                <Checkbox>
                  Desglosar IVA (el uso de CFDI S01 no desglosa IVA por defecto)
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item
              label="Descripción para productos sin mapeo"
              name="descripcionNoMapeados"
              extra="Texto que aparecerá para productos sin clasificación SAT"
            >
              <Input
                placeholder="CONSUMO DE ALIMENTOS"
                maxLength={200}
              />
            </Form.Item>
          </div>
        );

      case 2:
        return (
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
            <Title level={3}>¡Factura Generada Exitosamente!</Title>

            {facturaResult && (
              <>
                <Card style={{ marginTop: 24 }}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text>
                      <strong>Serie:</strong> {facturaResult.serie}
                    </Text>
                    <Text>
                      <strong>Folio:</strong> {facturaResult.folio}
                    </Text>
                    <Text>
                      <strong>Estado:</strong>{' '}
                      <Text
                        type={
                          facturaResult.estado === 'Timbrada'
                            ? 'success'
                            : facturaResult.estado === 'Cancelada'
                            ? 'danger'
                            : 'warning'
                        }
                      >
                        {facturaResult.estado}
                      </Text>
                    </Text>
                    <Text>
                      <strong>Total:</strong> ${facturaResult.total.toFixed(2)}
                    </Text>
                    {facturaResult.uuid && (
                      <>
                        <Divider />
                        <Text>
                          <strong>UUID:</strong> <br />
                          <Text copyable style={{ fontSize: 12 }}>
                            {facturaResult.uuid}
                          </Text>
                        </Text>
                        {facturaResult.fechaTimbrado && (
                          <Text>
                            <strong>Fecha Timbrado:</strong>{' '}
                            {formatInTimeZone(facturaResult.fechaTimbrado, 'DD/MM/YYYY HH:mm:ss')}
                          </Text>
                        )}
                        {facturaResult.rfcProvCertif && (
                          <Text>
                            <strong>PAC:</strong> {facturaResult.rfcProvCertif}
                          </Text>
                        )}
                      </>
                    )}

                    <Divider />

                    {/* Botones de descarga */}
                    <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                      <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={handleDownloadPDF}
                        size="large"
                        style={{ width: isMobile ? '100%' : 'auto' }}
                      >
                        Descargar PDF
                      </Button>

                      {facturaResult.xmlUrl && (
                        <Button
                          type="default"
                          icon={<FileZipOutlined />}
                          onClick={handleDownloadXML}
                          size="large"
                          style={{ width: isMobile ? '100%' : 'auto' }}
                        >
                          Descargar XML
                        </Button>
                      )}
                    </Space>

                    {/* Botones de timbrado/cancelación */}
                    {facturaResult.estado !== 'Cancelada' && facturaResult.estado !== 'CancelacionEnProceso' && (
                      <>
                        <Divider />
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                          {!facturaResult.uuid && (
                            <Button
                              type="primary"
                              icon={<SafetyCertificateOutlined />}
                              onClick={handleTimbrarFactura}
                              loading={timbrandoFactura}
                              size="large"
                              style={{ width: isMobile ? '100%' : 'auto', backgroundColor: '#1890ff' }}
                            >
                              Timbrar Factura
                            </Button>
                          )}

                          {facturaResult.uuid && puedeCancelar && (
                            <Button
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={handleCancelarFactura}
                              loading={cancelandoFactura}
                              size="large"
                              style={{ width: isMobile ? '100%' : 'auto' }}
                            >
                              Cancelar Factura
                            </Button>
                          )}
                        </Space>
                      </>
                    )}

                    {/* Mensaje informativo para facturas no timbradas */}
                    {!facturaResult.uuid && facturaResult.estado === 'Borrador' && (
                      <Alert
                        message="Factura en borrador"
                        description="Esta factura aún no ha sido timbrada fiscalmente. Puede timbrarla ahora o más tarde desde el sistema."
                        type="info"
                        showIcon
                        style={{ marginTop: 16, textAlign: 'left' }}
                      />
                    )}

                    {/* Mensaje para facturas canceladas */}
                    {facturaResult.estado === 'Cancelada' && (
                      <Alert
                        message="Factura cancelada"
                        description="Esta factura ha sido cancelada ante el SAT y ya no es válida fiscalmente."
                        type="error"
                        showIcon
                        style={{ marginTop: 16, textAlign: 'left' }}
                      />
                    )}
                  </Space>
                </Card>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    const isLoading = creatingCliente || solicitandoFactura || regenerandoFactura || facturandoDeGlobal;

    if (currentStep === 2) {
      return [
        <Button key="close" type="primary" onClick={handleClose}>
          Cerrar
        </Button>,
      ];
    }

    return [
      <Button key="cancel" onClick={onCancel}>
        Cancelar
      </Button>,
      currentStep > 0 && currentStep < 2 && (
        <Button key="back" onClick={() => setCurrentStep(currentStep - 1)}>
          Anterior
        </Button>
      ),
      currentStep === 0 && (
        <Button
          key="next"
          type="primary"
          onClick={handleStep1Next}
          disabled={!selectedCliente && !isNewCliente}
        >
          Siguiente
        </Button>
      ),
      currentStep === 1 && (
        <Button
          key="submit"
          type="primary"
          onClick={handleSolicitarFactura}
          loading={isLoading}
          icon={<FileTextOutlined />}
        >
          {isGlobalMode ? 'Facturar y reexpedir la global' : isRegenerateMode ? 'Regenerar Factura' : 'Generar Factura'}
        </Button>
      ),
    ];
  };

  if (!order) return null;

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>{isGlobalMode ? 'Facturar a nombre del cliente' : isRegenerateMode ? 'Regenerar Factura' : 'Solicitar Factura'}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={renderFooter()}
      width={isMobile ? '100%' : 800}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }} size={isMobile ? 'small' : 'default'}>
        <Step title="Cliente" icon={<UserOutlined />} />
        <Step title="Datos" icon={<DollarOutlined />} />
        <Step title="Confirmación" icon={<CheckCircleOutlined />} />
      </Steps>

      <Form form={form} layout="vertical">
        {renderStepContent()}
      </Form>

      <CancelarFacturaModal
        open={mostrarCancelacion}
        folio={facturaResult?.folio}
        // Sin esto el botón sigue habilitado durante la mutación y cada clic manda otra
        // cancelación al SAT.
        confirmLoading={cancelandoFactura}
        onCancel={() => setMostrarCancelacion(false)}
        onConfirm={confirmarCancelacion}
      />
    </Modal>
  );
};

export default SolicitarFacturaModal;
